import { Link } from 'react-router-dom';
import { SITE } from '@config/siteConfig';
import {
  HERO_BG,
  INTRO_LANDSCAPE,
  POURQUOI_IMAGE,
  ACTION_IMAGE,
  ACTION_GARANTIR,
  ACTION_CONNECTER,
  PRODUCT_PIMENTS,
  PRODUCT_GOMBO,
  PRODUCT_CACAHOUETES,
  GALLERY_LEFT,
  GALLERY_RIGHT,
} from '@config/assets';
import styles from './Home.module.css';

export default function Home() {
  return (
    <>
      {/* ============================================
          1. HERO
          ============================================ */}
      <section className={styles.hero}>
        <img
          src={HERO_BG}
          alt="Haitian countryside"
          className={styles.heroBg}
        />
        <div className={styles.heroDimmer} />
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{SITE.name}</h1>
          <p className={styles.heroSubtitle}>{SITE.tagline}</p>
        </div>
      </section>

      {/* ============================================
          2. INTRO
          ============================================ */}
      <section className={styles.intro}>
        <div className={styles.container}>
          <h2 className={styles.introTitle}>{SITE.name}</h2>
          <p className={styles.introSubtitle}>
            {SITE.tagline}.&nbsp;{SITE.description}
          </p>
        </div>

        <div className={styles.container}>
          <div className={styles.introImageWrap}>
            <img
              src={INTRO_LANDSCAPE}
              alt="Agricultural produce baskets"
              className={styles.introImage}
            />
          </div>
        </div>

        <div className={styles.container}>
          <p className={styles.introBody}>
            Né en 2019 dans la région du Bas Plateau, Bohío Kiskeya rassemble
            des producteurs engagés afin de faciliter la circulation des
            produits agricoles entre les régions rurales et les marchés urbains,
            tout en contribuant à la souveraineté alimentaire du territoire
            haïtien.
          </p>
        </div>
      </section>

      {/* ============================================
          3. POURQUOI BOHÍO KISKEYA
          ============================================ */}
      <section className={styles.pourquoi}>
        <div className={`${styles.container} ${styles.pourquoiGrid}`}>
          <div className={styles.pourquoiCopy}>
            <h2 className={styles.sectionHeading}>Pourquoi Bohío Kiskeya</h2>
            <p className={styles.pourquoiSubtitle}>
              Une agriculture riche, des marchés éloignés.
            </p>
            <div className={styles.pourquoiBody}>
              <p>
                Haïti possède une diversité agricole remarquable. Pourtant, de
                nombreux producteurs restent isolés des circuits de distribution
                structurés.
              </p>
              <p>
                Les défis logistiques, l&rsquo;absence d&rsquo;organisation
                collective et la difficulté d&rsquo;accès aux marchés limitent
                la valorisation des récoltes.
              </p>
              <p>
                Bohío Kiskeya agit comme facilitateur entre producteurs,
                distributeurs et partenaires afin de structurer durablement les
                échanges agricoles.
              </p>
            </div>
          </div>
          <div className={styles.pourquoiImageWrap}>
            <img
              src={POURQUOI_IMAGE}
              alt="Farmer overlooking fields"
              className={styles.pourquoiImage}
            />
          </div>
        </div>

        <div className={`${styles.container} ${styles.pourquoiButtons}`}>
          <Link to="/rejoindre" className={styles.btnBlack}>
            Rejoindre le réseau d&rsquo;agriculteurs
          </Link>
          <Link to="/partenaire" className={styles.btnGray}>
            Devenir partenaire
          </Link>
        </div>
      </section>

      {/* ============================================
          4. NOTRE ACTION
          ============================================ */}
      <section className={styles.action}>
        <div className={`${styles.container} ${styles.actionTitle}`}>
          <h2 className={styles.sectionHeading}>Notre action</h2>
        </div>

        {/* Row 1: Structurer la production – text left, image right */}
        <div className={`${styles.container} ${styles.actionRow}`}>
          <div className={styles.actionBlock}>
            <h3 className={styles.actionItemTitle}>Structurer la production</h3>
            <p className={styles.actionItemText}>
              Nous accompagnons les producteurs dans l&rsquo;organisation
              collective des récoltes et le regroupement des volumes afin de
              répondre aux besoins des marchés.
            </p>
          </div>
          <div className={styles.actionImgStructurer}>
            <img
              src={ACTION_IMAGE}
              alt="Structurer la production"
              className={styles.actionImg}
            />
          </div>
        </div>

        {/* Row 2: Garantir la qualité – image left, text right */}
        <div className={`${styles.container} ${styles.actionRow}`}>
          <div className={styles.actionImgGarantir}>
            <img
              src={ACTION_GARANTIR}
              alt="Garantir la qualité"
              className={styles.actionImg}
            />
          </div>
          <div className={styles.actionBlock}>
            <h3 className={styles.actionItemTitle}>Garantir la qualité</h3>
            <p className={styles.actionItemText}>
              Une charte commune encadre les engagements des producteurs membres
              autour de la sélection des produits, de leur traçabilité et du
              respect des pratiques agricoles responsables.
            </p>
          </div>
        </div>

        {/* Row 3: Connecter les marchés – text left, image right */}
        <div className={`${styles.container} ${styles.actionRow}`}>
          <div className={styles.actionBlock}>
            <h3 className={styles.actionItemTitle}>Connecter les marchés</h3>
            <p className={styles.actionItemText}>
              Bohío Kiskeya facilite le transport et la distribution entre
              régions agricoles, villes et partenaires commerciaux.
            </p>
          </div>
          <div className={styles.actionImgConnecter}>
            <img
              src={ACTION_CONNECTER}
              alt="Connecter les marchés"
              className={styles.actionImg}
            />
          </div>
        </div>

        {/* En savoir plus button */}
        <div className={styles.container}>
          <Link to="/nos-origines" className={styles.btnBlack}>
            En savoir plus
          </Link>
        </div>
      </section>

      {/* ============================================
          5. NOS PRODUITS
          ============================================ */}
      <section className={styles.produits}>
        <div className={styles.container}>
          <h2 className={styles.produitsTitle}>Nos produits</h2>
          <div className={styles.cards}>
            {PRODUCTS.map(({ title, subtitle, image }) => (
              <div key={title} className={styles.card}>
                <div className={styles.cardImageWrap}>
                  <img
                    src={image}
                    alt={title}
                    className={styles.cardImage}
                  />
                </div>
                <div className={styles.cardCopy}>
                  <p className={styles.cardTitle}>{title}</p>
                  {/* <p className={styles.cardSubtitle}>{subtitle}</p> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          6. GALLERY
          ============================================ */}
      <section className={styles.gallery}>
        <div className={`${styles.container} ${styles.galleryGrid}`}>
          <div className={styles.galleryImageWrap}>
            <img
              src={GALLERY_LEFT}
              alt="Agricultural scene"
              className={styles.galleryImage}
            />
          </div>
          <div className={styles.galleryImageWrap}>
            <img
              src={GALLERY_RIGHT}
              alt="Agricultural scene"
              className={styles.galleryImage}
            />
          </div>
        </div>
      </section>

      {/* ============================================
          7. NOUS REJOINDRE (CTA)
          ============================================ */}
      <section className={styles.rejoindre}>
        <div className={`${styles.container} ${styles.rejoindreInner}`}>
          <h2 className={styles.sectionHeading}>Nous rejoindre</h2>
          <div className={styles.rejoindreButtons}>
            <Link to="/rejoindre" className={styles.btnBlack}>
              Rejoindre le réseau d&rsquo;agriculteurs
            </Link>
            <Link to="/partenaire" className={styles.btnGray}>
              Devenir partenaire
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* ---- Static product data ---- */
const PRODUCTS = [
  {
    title: 'Piments',
    subtitle: 'Author',
    image: PRODUCT_PIMENTS,
  },
  {
    title: 'Kalalou gombo',
    subtitle: 'Author',
    image: PRODUCT_GOMBO,
  },
  {
    title: 'Cacahouetes',
    subtitle: 'Author',
    image: PRODUCT_CACAHOUETES,
  },
];
